import React, { useEffect, useState, useMemo } from "react";
import CustomChart from "../Charts";
import styles from "../../pages/Welcome/style.module.scss";

/**
 * KPIDoughnutChart - Renders a doughnut chart for KPI data
 *
 * @param {Object} props
 * @param {Object} props.data - The chart data
 * @param {string} props.title - Optional chart title
 * @param {boolean} [props.showLegend] - Chart.js legend on the right (default false)
 * @param {Array} [props.customLabels] - Per-slice second line, e.g. raw counts; omit duplicate % strings
 * @param {boolean} [props.valueAsPercent] - Format slice values as a single "n.n%" label (no customLabels)
 * @param {boolean} [props.hideZeroLabels] - Hide labels for zero-value slices
 * @param {function(boolean): void} [props.onSliceClick] - Click handler; arg is true for the second slice (e.g. Publish / Open case / Take action), false for the first
 */
const KPIDoughnutChart = ({
  data,
  theme,
  showLegend,
  customLabels,
  valueAsPercent,
  hideZeroLabels,
  onSliceClick,
}) => {
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState(null);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      ...(onSliceClick
        ? {
            onClick: (_event, elements) => {
              if (!elements?.length) return;
              const idx = elements[0]?.index;
              if (idx !== 0 && idx !== 1) return;
              onSliceClick(idx === 1);
            },
            onHover: (event, elements) => {
              const canvas = event?.native?.target;
              if (canvas && canvas.style) {
                canvas.style.cursor = elements?.length ? "pointer" : "default";
              }
            },
          }
        : {}),
      plugins: {
        legend: {
          display: Boolean(showLegend),
          position: "right",
          labels: {
            usePointStyle: true,
            color: theme === "dark" ? "white" : "black",
            font: {
              size: 12,
              color: theme === "dark" ? "white" : "black",
            },
            pointStyle: "circle",
            pointStyleWidth: 16,
            padding: 20,
            boxWidth: 12,
          },
        },
        datalabels: {
          color: "white",
          textStrokeColor: "rgba(0, 0, 0, 0.35)",
          textStrokeWidth: 2,
          font: {
            size: 11,
            weight: 600,
          },
          display: (context) => {
            if (!hideZeroLabels) return true;
            const value = Number(context.dataset?.data?.[context.dataIndex]);
            return Number.isFinite(value) ? value > 0 : true;
          },
          formatter: (value, context) => {
            const num = Number(value);
            if (hideZeroLabels && Number.isFinite(num) && num <= 0) {
              return "";
            }
            const pctStr = `${Number.isFinite(num) ? num.toFixed(1) : value}%`;

            if (customLabels && customLabels[context.dataIndex] !== undefined) {
              const raw = customLabels[context.dataIndex];
              const countStr =
                typeof raw === "number" ? String(raw) : String(raw);
              const nPct = pctStr.replace(/\s/g, "");
              const nCount = countStr.replace(/\s/g, "");
              // Same value twice, e.g. 42.1% (42.1%) — show one line only
              if (
                nCount === nPct ||
                nCount === `${Number.isFinite(num) ? num.toFixed(1) : value}%`
              ) {
                return pctStr;
              }
              return [pctStr, `(${countStr})`];
            }
            if (valueAsPercent) {
              return pctStr;
            }
            return value;
          },
          display: true,
          anchor: "center",
          align: "center",
        },
      },
      elements: {
        arc: {
          borderWidth: 0,
        },
      },
      backgroundColor: "transparent",
      layout: {
        padding: {
          left: 10,
          right: 10,
          top: 10,
          bottom: 10,
        },
      },
    }),
    [theme, showLegend, customLabels, valueAsPercent, hideZeroLabels, onSliceClick],
  );

  useEffect(() => {
    try {
      const processedData = data.datasets
        ? data
        : {
            labels: data.labels || [],
            datasets: [
              {
                data: data.values || [],
                backgroundColor: data.backgroundColor || [],
                borderWidth: 0,
              },
            ],
          };

      setChartData(processedData);
      setError(null);
    } catch (err) {
      console.error("Error processing chart data:", err);
      setError("Failed to process chart data");
    }
  }, [data]);

  const chartDependency = useMemo(
    () => ({ chartData, options }),
    [chartData, options],
  );

  if (error) {
    return <div className={styles.chartError}>Error: {error}</div>;
  }

  if (!chartData) {
    return <div className={styles.chartLoading}>Loading chart...</div>;
  }

  return (
    <div className={styles.chartContainer}>
      <div
        style={{
          height: showLegend ? "240px" : "200px",
          width: "100%",
        }}
      >
        <CustomChart
          type="doughnut"
          data={chartData}
          options={options}
          theme={theme}
          dependency={chartDependency}
        />
      </div>
    </div>
  );
};

export default KPIDoughnutChart;
